<template>
    <custom-table
        :title="$t('pages.stock.title')"
        :subTitle="$t('pages.stock.subTitle')"
        rowKey="id"
        :items="table.data"
        :loading="table.loading"
        selectableRows
        :columns="fields"
        :actions="actions"
        :pagination="table.pagination"
        :sortMeta="table.pagination.sort || []"
        changeUrlPage
        @action="handleClickAction"
        @selectableRows="handleSelectedRow"
        @changeTable="handleTableChange"
        @sort="handleSort"
    >
        <template v-slot:category="{ row: record }">
            {{ record.category?.name || '-' }}
        </template>
        <template v-slot:total_quantity="{ row: record }">
            <span class="stk-list-qty" :class="lowStockClass(record)">
                {{ formatQty(record.total_quantity) }}
            </span>
            <span class="stk-list-unit" v-if="record.unit_type">{{ record.unit_type.name }}</span>
            <span v-if="isLowStock(record)" class="stk-list-low-pill">{{ $t('pages.stock.lowStockBadge') }}</span>
        </template>
        <template v-slot:sale_price="{ row: record }">
            <span class="stk-list-price">{{ formatTRY(record.sale_price) }}</span>
        </template>
        <template v-slot:actions="{ row: record }">
            <div class="d-flex justify-content-end">
                <el-tooltip :content="$t('common.detail')" placement="top">
                    <router-link :to="'/stock/detail/'+record.id" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1">
                         <span class="svg-icon svg-icon-3">
                             <inline-svg src="/media/icons/duotune/general/gen004.svg"/>
                         </span>
                    </router-link>
                </el-tooltip>
                <el-tooltip :content="$t('common.delete')" placement="top">
                    <el-popconfirm :title="$t('messages.sureDelete')" :confirm-button-text="$t('btn.yes')" :cancel-button-text="$t('btn.no')" @confirm="deleteRecord([record.id])">
                        <template #reference>
                            <a class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm">
                                <span class="svg-icon svg-icon-3">
                                    <inline-svg src="/media/icons/duotune/general/gen027.svg"/>
                                </span>
                            </a>
                        </template>
                    </el-popconfirm>
                </el-tooltip>
            </div>
        </template>
    </custom-table>
</template>

<script>
import CustomTable from "@/components/custom-table";

export default {
    name: "stock",
    components: {
        CustomTable
    },
    data() {
        return {
            fields: [
                {
                    name: this.$t("pages.stock.cols.id"),
                    key: "id",
                    class: ''
                },
                {
                    name: this.$t("pages.stock.cols.name"),
                    key: "name",
                    sortingField: "name",
                    sortable: true,
                },
                {
                    name: this.$t("pages.stock.cols.category"),
                    scopedSlots: {customRender: "category"}
                },
                {
                    name: this.$t("pages.stock.cols.sale_price"),
                    sortingField: "sale_price",
                    sortable: true,
                    scopedSlots: {customRender: "sale_price"},
                },
                {
                    name: this.$t("pages.stock.cols.total_quantity"),
                    scopedSlots: {customRender: "total_quantity"}
                },
                {
                    name: this.$t("common.action"),
                    key: "action",
                    scopedSlots: {customRender: "actions"}
                }
            ],
            actions: [
                {
                    name: "new",
                    icon: "bi-plus-lg",
                    label: "btn.add"
                },
                {
                    name: "refresh",
                    icon: "bi-arrow-repeat",
                    label: "btn.refresh"
                },
                {
                    name: "delete",
                    icon: "bi-trash",
                    label: "btn.delete"
                }
            ],
            form: {},
            selectedRowKeys: [],
            filterParameters: {},
        }
    },
    computed: {
        table() {
            return this.$store.state.stock.table;
        }
    },
    created() {
    },
    mounted() {
        this.setCurrentPageBreadcrumbs(this.$t("pages.stock.title"), []);

        this.filterParameters = {
            sort: 'created_at:desc'
        };


        this.refreshTable();
    },
    methods: {
        formatTRY(amount) {
            const n = parseFloat(amount);
            if (isNaN(n)) return '-';
            return n.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 });
        },
        formatQty(q) {
            const n = parseFloat(q);
            if (isNaN(n)) return '0,00';
            return n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        isLowStock(record) {
            const min = parseFloat(record?.min_quantity);
            const cur = parseFloat(record?.total_quantity);
            if (isNaN(min) || isNaN(cur)) return false;
            if (min <= 0) return false;
            return cur < min;
        },
        lowStockClass(record) {
            return this.isLowStock(record) ? 'stk-list-qty--warn' : '';
        },
        handleClickAction(name) {
            switch (name) {
                case "refresh":
                    this.refreshTable();
                    break;

                case "new":
                    this.$router.push({
                        path: "/stock/detail/new"
                    });
                    break;

                case "delete":
                    this.deleteRecord(this.selectedRowKeys.flat());
                    break;

                default:
                    break;
            }
        },
        handleTableChange(pagination, filterData) {
            const currentSort = this.filterParameters.sort || 'created_at:desc';
            this.filterParameters = { ...filterData, sort: currentSort };

            this.$store.dispatch("stock/get", {
                page: pagination,
                filterData: this.filterParameters
            });
        },
        handleSort({ columnName, order }) {
            this.filterParameters = {
                ...this.filterParameters,
                sort: `${columnName}:${order}`,
            };
            this.$store.dispatch("stock/get", {
                page: { current: this.$route.query.page },
                filterData: this.filterParameters,
            });
        },
        handleSelectedRow(record) {
            this.selectedRowKeys = record;
        },
        deleteRecord(id) {
            this.$store.dispatch("stock/delete", {
                id: id
            }).then((successDeleted) => {
                this.refreshTable();
            });
        },
        refreshTable() {
            this.$store.dispatch('stock/get', {
                page: {
                    pageSize: 25,
                    current: this.$route.query.page
                },
                filterData: Object.assign(this.filterParameters, this.$root.getFilterWithUrl({}))
            });
        }
    }
}
</script>

<style scoped>
.stk-list-qty {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    font-size: 13.5px;
    margin-right: 4px;
}

.stk-list-qty--warn { color: #d97706; }

.stk-list-unit {
    font-size: 11px;
    color: #8e8e93;
}

.stk-list-low-pill {
    margin-left: 6px;
    display: inline-flex;
    padding: 2px 7px;
    border-radius: 999px;
    background: rgba(217, 119, 6, 0.12);
    color: #d97706;
    font-size: 9.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.stk-list-price {
    font-variant-numeric: tabular-nums;
    font-weight: 600;
}

html.dark .stk-list-qty--warn,
html.midnight .stk-list-qty--warn,
html.dark .stk-list-low-pill,
html.midnight .stk-list-low-pill { color: #FBBF24; }

html.dark .stk-list-unit,
html.midnight .stk-list-unit { color: #9A9CAE; }
</style>